import { apiClient, unwrap } from '@/lib/apiClient';
import type { ApiSuccess } from '@/types/api';
import type {
  CreateSalaryComponentPayload,
  MonthPayrollSummary,
  SalaryComponent,
  SalaryPayload,
  SalaryPayment,
  SalaryPaymentMonth,
  SalaryPaymentPayload,
  StaffSalaryRecord,
  UpdateSalaryComponentPayload,
} from '@/types/payroll';

/**
 * Every call the payroll screens make.
 *
 * No institute id in any path — the server takes it from the caller's token. A
 * member of staff is addressed by their account uuid, as on every staff route;
 * one salary by its own uuid.
 */
export const payrollApi = {
  listComponents: () =>
    unwrap(apiClient.get<ApiSuccess<SalaryComponent[]>>('/institute/payroll/components')),

  createComponent: (payload: CreateSalaryComponentPayload) =>
    unwrap(apiClient.post<ApiSuccess<SalaryComponent>>('/institute/payroll/components', payload)),

  updateComponent: (uuid: string, payload: UpdateSalaryComponentPayload) =>
    unwrap(
      apiClient.patch<ApiSuccess<SalaryComponent>>(`/institute/payroll/components/${uuid}`, payload),
    ),

  removeComponent: (uuid: string) =>
    unwrap(apiClient.delete<ApiSuccess<null>>(`/institute/payroll/components/${uuid}`)),

  /** Adds whichever common heads are missing. Safe to call twice. */
  addDefaultComponents: () =>
    unwrap(apiClient.post<ApiSuccess<SalaryComponent[]>>('/institute/payroll/components/defaults')),

  getStaffSalary: (staffUuid: string) =>
    unwrap(
      apiClient.get<ApiSuccess<StaffSalaryRecord>>(`/institute/payroll/staff/${staffUuid}/salary`),
    ),

  /** A new salary from its own date — the joining salary, or an increment. */
  setStaffSalary: (staffUuid: string, payload: SalaryPayload) =>
    unwrap(
      apiClient.post<ApiSuccess<StaffSalaryRecord>>(
        `/institute/payroll/staff/${staffUuid}/salary`,
        payload,
      ),
    ),

  /** Corrects the latest salary. The server keeps the old one on record. */
  correctSalary: (salaryUuid: string, payload: SalaryPayload) =>
    unwrap(
      apiClient.put<ApiSuccess<StaffSalaryRecord>>(`/institute/payroll/salaries/${salaryUuid}`, payload),
    ),

  /** Withdraws the latest salary and puts the one before it back in force. */
  withdrawSalary: (salaryUuid: string) =>
    unwrap(
      apiClient.delete<ApiSuccess<StaffSalaryRecord>>(`/institute/payroll/salaries/${salaryUuid}`),
    ),

  /** This month across the whole staff room: owed, paid and still to go out. */
  getMonthSummary: () =>
    unwrap(apiClient.get<ApiSuccess<MonthPayrollSummary>>('/institute/payroll/month-summary')),

  /** The last five months: what was due, and what was paid, when and how. */
  listSalaryPayments: (staffUuid: string) =>
    unwrap(
      apiClient.get<ApiSuccess<{ months: SalaryPaymentMonth[] }>>(
        `/institute/payroll/staff/${staffUuid}/payments`,
      ),
    ),

  /** Records a month as paid. Refused if that month already is. */
  recordSalaryPayment: (staffUuid: string, payload: SalaryPaymentPayload) =>
    unwrap(
      apiClient.post<ApiSuccess<SalaryPayment>>(
        `/institute/payroll/staff/${staffUuid}/payments`,
        payload,
      ),
    ),

  /** Takes a wrongly recorded payment off, so the month can be entered again. */
  removeSalaryPayment: (paymentUuid: string) =>
    unwrap(apiClient.delete<ApiSuccess<null>>(`/institute/payroll/payments/${paymentUuid}`)),
};
